/**
 * The way in: signing in, registering, and setting a password from a link.
 *
 * The "waiting for approval" screen is not an error page. Registering is
 * supposed to end there — the account exists and does nothing until the admin
 * decides — so it says that plainly rather than looking like a failure.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError, api } from '../api/client'
import { useSession } from '../auth/session'
import { Button, Field, Problem } from '../ui'

export function SignIn() {
  const { signIn } = useSession()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
      navigate('/', { replace: true })
    } catch (problem) {
      // A pending or suspended account answers 403 with a code. That is not a
      // wrong password and must not be shown as one.
      if (problem instanceof ApiError && problem.status === 403) {
        navigate('/waiting', { state: { message: problem.message }, replace: true })
        return
      }
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="centered">
      <h1>bjsfabric</h1>
      <p className="lede">Sign in to see your requirements and enquiries.</p>
      <Problem error={error} />
      <form onSubmit={submit}>
        <Field label="Email">
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Sign in
          </Button>
          <Link className="button button-quiet" to="/register" style={{ lineHeight: '44px' }}>
            Register
          </Link>
        </div>
      </form>
      <p className="field-hint" style={{ marginTop: 16 }}>
        <Link to="/forgot">Forgotten your password?</Link>
      </p>
    </div>
  )
}

export function Register() {
  const [form, setForm] = useState({
    organizationName: '',
    organizationKind: 'COMPANY',
    contactName: '',
    email: '',
    phone: '',
    password: '',
  })
  const [error, setError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(
        '/auth/register',
        { ...form, phone: form.phone || null },
        { anonymous: true },
      )
      setDone(true)
    } catch (problem) {
      setError(problem instanceof ApiError ? problem : null)
      if (!(problem instanceof ApiError)) throw problem
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="centered">
        <h1>Thank you</h1>
        <p className="lede">
          Your details are with the administrator. You will be able to sign in once he has
          approved you.
        </p>
        <Link className="button button-quiet" to="/signin" style={{ lineHeight: '44px' }}>
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <div className="centered">
      <h1>Register</h1>
      <p className="lede">Nothing is visible to you until the administrator approves it.</p>
      <Problem error={error} />
      <form onSubmit={submit}>
        <Field label="I am a" >
          <select value={form.organizationKind} onChange={set('organizationKind')}>
            <option value="COMPANY">Company — I make fabric</option>
            <option value="BUYER">Buyer — I need fabric</option>
          </select>
        </Field>
        <Field label="Company name" error={error?.issueFor('organizationName')}>
          <input required value={form.organizationName} onChange={set('organizationName')} />
        </Field>
        <Field label="Your name" error={error?.issueFor('contactName')}>
          <input required value={form.contactName} onChange={set('contactName')} />
        </Field>
        <Field label="Email" error={error?.issueFor('email')}>
          <input type="email" required value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Phone" hint="Optional" error={error?.issueFor('phone')}>
          <input value={form.phone} onChange={set('phone')} />
        </Field>
        <Field
          label="Password"
          hint="At least 12 characters. Length beats punctuation."
          error={error?.issueFor('password')}
        >
          <input
            type="password"
            autoComplete="new-password"
            required
            value={form.password}
            onChange={set('password')}
          />
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Register
          </Button>
          <Link className="button button-quiet" to="/signin" style={{ lineHeight: '44px' }}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  )
}

/** Where a rejected sign-in lands when the details were right but the account is not usable. */
export function Waiting() {
  const message =
    (window.history.state?.usr?.message as string | undefined) ??
    'Your account is waiting for approval.'

  return (
    <div className="centered">
      <h1>Not yet</h1>
      <p className="lede">{message}</p>
      <Link className="button button-quiet" to="/signin" style={{ lineHeight: '44px' }}>
        Back to sign in
      </Link>
    </div>
  )
}

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    try {
      await api.post('/auth/forgot-password', { email }, { anonymous: true })
    } finally {
      // Always the same outcome, known address or not: the server refuses to
      // say, and the screen must not say either.
      setSent(true)
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="centered">
        <h1>Check your messages</h1>
        <p className="lede">
          If that address has an account, a link to set a new password is on its way.
        </p>
        <Link className="button button-quiet" to="/signin" style={{ lineHeight: '44px' }}>
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <div className="centered">
      <h1>Forgotten password</h1>
      <form onSubmit={submit}>
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Send me a link
          </Button>
        </div>
      </form>
    </div>
  )
}

/** The screen an invitation link opens. Also used for a forgotten password. */
export function SetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const [invite, setInvite] = useState<{ email: string; displayName: string } | null>(null)
  // Distinct from "still loading": a token that turns out expired or already
  // spent must replace the whole screen, not just leave the name blank.
  const [inviteInvalid, setInviteInvalid] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return
    void (async () => {
      try {
        setInvite(
          await api.get<{ email: string; displayName: string }>(
            `/auth/password-setup/${encodeURIComponent(token)}`,
          ),
        )
      } catch {
        setInviteInvalid(true)
      }
    })()
  }, [token])

  // Only checked once there's something to compare against, so the field
  // doesn't turn red before anyone has typed a single character into it.
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (mismatch) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/reset-password', { token, password }, { anonymous: true })
      navigate('/signin', { replace: true })
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  if (!token || inviteInvalid) {
    return (
      <div className="centered">
        <h1>Link incomplete</h1>
        <p className="lede">
          {token
            ? 'That link has expired or already been used. Ask the administrator to send another.'
            : 'That link is missing its token. Ask the administrator to send it again.'}
        </p>
      </div>
    )
  }

  return (
    <div className="centered">
      <h1>Choose a password</h1>
      <p className="lede">This link works once.</p>
      {invite ? (
        <p className="lede">
          {invite.displayName} <span className="field-hint">{invite.email}</span>
        </p>
      ) : null}
      <Problem error={error} />
      <form onSubmit={submit}>
        <Field label="New password" hint="At least 12 characters.">
          <input
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <Field label="Confirm password" error={mismatch ? "Passwords don't match" : undefined}>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy} disabled={mismatch}>
            Save and sign in
          </Button>
        </div>
      </form>
    </div>
  )
}
